import { useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Card from "../../common/components/Card";
import { DefaultButton } from "../../common/components/DefaultButton";
import { Col, Container, Row } from "../../common/components/Grid";
import { cdate } from "../../common/utils/CDate";
import { safeEval } from "../../common/utils/safeEval";
import { dynamicSort } from "../../common/utils/tableUtilities";
import { useEpcrVitalDefs } from "../../reducers/epcrVitalSlice";
import { VitalsDialog } from "../VitalsDialog";
import { useEpcrEditorEpcr, useEpcrEditorVitals } from "./EpcrContext";
import PropTypes from 'prop-types';
import { deepClone } from "../../common/utils/objectUtilities";

const COL_WIDTH = '96px';

const EpcrVitals = ({ disabled = false }) => {
    const { t } = useTranslation();
    const { epcr } = useEpcrEditorEpcr();
    const { vitals, addVital, updateVital, removeVital } = useEpcrEditorVitals();
    const vitalDefs = useEpcrVitalDefs();
    const [showDialog, setShowDialog] = useState(false);
    const [vitalID, setVitalID] = useState(null);
    const displayVitalDefs = useMemo(() => {
        let defs = deepClone(vitalDefs);
        defs.sort(dynamicSort(['group','sequence','type']));
        defs.forEach(v => v.tostring = safeEval(v.tostring));
        return defs;
    }, [vitalDefs])

    let selectedVital = useMemo(() => vitals?.find(v => v.id === vitalID), [vitals, vitalID]);

    let displayVitals = useMemo(() => {
        let displayVitals = deepClone(vitals);
        displayVitals.sort(dynamicSort(['timestamp']));
        return displayVitals;
    }, [vitals])

    const genVital = () => {
        let newVital = { id: crypto.randomUUID(), pcrid: epcr.id, values: {} }
        vitalDefs?.forEach(def => {
            newVital.values[def.type] = {};
        })
        return newVital;
    }

    let handleShowDialog = (vital) => {
        setShowDialog(true);
        if (vital) {
            setVitalID(vital.id)
        }
    }

    let handleCloseDialog = () => {
        setShowDialog(false);
        setVitalID(null);
    }

    const handleUpdate = async (field, value) => {
        if (!vitalID) {
            let newVital = { ...genVital(), [field]: value};
            await addVital(newVital);
            setVitalID(newVital.id);
        } else {
            updateVital(selectedVital, field, value);
        }             
    }

    const handleRemove = () => {
        if (!vitalID) {
            return;
        }
        removeVital(vitalID);
        handleCloseDialog();
    }

    const getVitalStyle = (def, v) => {
        if (def && v && Object.keys(v).length > 0) {
            let style = def.tostring(v)?.style ?? {};
            // replace '-' with camel case for styles
            let newStyle = {};
            Object.keys(style).forEach(key => {
                let parts = key.split('-');
                for (let i = 1; i < parts.length; ++i) {
                    parts[i] = parts[i].charAt(0).toUpperCase() + parts[i].slice(1);
                }
                let newKey = parts.join('');
                newStyle[newKey] = style[key];
            })
            return {...newStyle, cursor: 'pointer'};
        } else {
            return { cursor: 'pointer' }
        }
    }

    const getVitalText = (def, v) => {
        if (def && v && Object.keys(v).length > 0) {
            let text = def.tostring(v)?.text;
            return text ?? '?'
        } else {
            return ' - '
        }
    }

    let header = [<th key={'vitalheader_timeOfDay'} className="position-sticky" style={{width: COL_WIDTH, left: '0px', backgroundColor: 'white'}}>{t('timeOfDay')}</th>, ...displayVitals.map(v => <th key={'vitalheader_' + v.id.toString().replaceAll('-','')} style={{cursor: 'pointer', width: COL_WIDTH}} ><button type="button" style={{ color: 'inherit', backgroundColor: 'inherit', textAlign: "start", border: "none", fontWeight: 'bold'}} onClick={() => handleShowDialog(v)}>{cdate(v.timestamp).timeFormat()}</button></th>)];
    let rows = displayVitalDefs.map((def, idx) => {
        return <tr key={'vital_' + def.shortname}>
            <td className="position-sticky font-weight-bold" style={{width: COL_WIDTH, left: '0px', backgroundColor: idx % 2 ? 'white' : '#F2F2F2'}} >{def.shortname}</td>
            {displayVitals.map(v => <td key={'vital_' + def.shortname + '_' + v.id.toString().replaceAll('-', '')} style={{width: '80px'}}>
                <button type="button" style={{ color: 'inherit', backgroundColor: 'inherit', textAlign: "start", border: "none", ...getVitalStyle(def, v.values[def.type])}} onClick={() => handleShowDialog(v)}>
                    {getVitalText(def, v.values[def.type])}
                </button>
            </td>)}
        </tr>
    }) 

    return <>
        <VitalsDialog showDialog={showDialog} onClose={handleCloseDialog} disabled={disabled} vital={selectedVital} onChange={handleUpdate} onRemove={handleRemove}></VitalsDialog>
        <Container fluid>
            <Card>
                <Row>
                    {!disabled ? <Col style={{textAlign: "end"}}><DefaultButton kind="add" onClick={_ => handleShowDialog(null)}></DefaultButton></Col> : null}
                </Row>
                <Row>
                    <Col style={{overflowX: 'auto'}}>
                        <Table className="table-sm table-striped" style={{ tableLayout: 'fixed'}}>
                            <thead><tr>{header}</tr></thead>
                            <tbody>{rows}</tbody>
                        </Table>
                    </Col>
                </Row>
            </Card>
        </Container>
    </>
}

EpcrVitals.propTypes = {
    disabled: PropTypes.bool
}

export default EpcrVitals;